import { useEffect, useRef } from "react";
import * as d3 from "d3";
import { apiFetch } from "../../api/apiClient";

/* =========================
   CONSTANTS
   ========================= */
const HEIGHT = 520;

const margin = {
  top: 50,
  right: 15,
  bottom: 80,
  left: 80,
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 20;
// Multiplicative step used by the keyboard +/- zoom controls.
const ZOOM_STEP = 1.5;

export default function InverterMpptProfile({
  portfolioName = "",
  duration = "month",
  startDate = "",
  endDate = "",
  powerblockId = "",
  data = [],
  onTooltip,
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const dataRef = useRef(null);
  // Persist the current zoom transform across resizes / data updates so the
  // user's view is preserved when the chart re-renders.
  const transformRef = useRef(d3.zoomIdentity);
  // Persistent click/keyboard selection — kept across draws so the user's
  // pick survives resizes, focus/blur, and data refreshes.
  const selectedRef = useRef(null);
  // Latest tooltip setter, read at hover/keyboard time so the draw effect
  // doesn't tear down just because the parent passed a new callback identity.
  const onTooltipRef = useRef(onTooltip);
  onTooltipRef.current = onTooltip;

  useEffect(() => {
    if (!containerRef.current) return;

    // Tooltip div fallback (only when page-level ChartTooltip isn't used)
    const tooltip = onTooltipRef.current
      ? null
      : d3
          .select("body")
          .append("div")
          .style("position", "absolute")
          .style("left", "0")
          .style("top", "0")
          .style("background", "white")
          .style("border", "1px solid #ccc")
          .style("padding", "6px")
          .style("border-radius", "4px")
          .style("pointer-events", "none")
          .style("box-shadow", "0 0 10px rgba(0,0,0,0.25)")
          .style("opacity", 0);

    // Bridge ref so the effect-scope keyboard/focus listeners (which
    // outlive a single draw) can always drive the most recent chart
    // instance. Populated at the end of every `draw()` call.
    const handlersRef = { current: null };

    /* =========================
       DRAW (LAYOUT ONLY)
       ========================= */
    function draw() {
      if (!containerRef.current || !dataRef.current) return;

      const width = containerRef.current.getBoundingClientRect().width;

      const innerWidth = width - margin.left - margin.right;
      const innerHeight = HEIGHT - margin.top - margin.bottom;

      const svg = d3
        .select(svgRef.current)
        .attr("width", width)
        .attr("height", HEIGHT);

      svg.selectAll("*").remove();

      // Clip path so dots & overlays stay inside the chart area when zoomed.
      svg
        .append("defs")
        .append("clipPath")
        .attr("id", "mppt-chart-clip")
        .append("rect")
        .attr("width", innerWidth)
        .attr("height", innerHeight);

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      /* =========================
         BASE SCALES
         (xBase / yBase never change — zoom rescales them per-render.)
         ========================= */
      const xBase = d3.scaleLinear()
        .domain([0, 1200])
        .range([0, innerWidth]);

      const yBase = d3.scaleLinear()
        .domain([0, 1400])
        .range([innerHeight, 0]);

      /* =========================
         LAYER GROUPS
         ========================= */
      const gridYG = g.append("g").attr("class", "grid grid-y");
      const gridXG = g
        .append("g")
        .attr("class", "grid grid-x")
        .attr("transform", `translate(0,${innerHeight})`);

      const axisYG = g.append("g").attr("class", "axis axis-y");
      const axisXG = g
        .append("g")
        .attr("class", "axis axis-x")
        .attr("transform", `translate(0,${innerHeight})`);

      const plotArea = g
        .append("g")
        .attr("class", "plot-area")
        .attr("clip-path", "url(#mppt-chart-clip)");

      const dotsG = plotArea.append("g").attr("class", "dots");
      // Active hover dot lives inside the clipped plot area so it can't
      // visually escape the chart when zoomed in.
      const activeDot = plotArea
        .append("circle")
        .attr("class", "active-dot")
        .attr("r", 7)
        .attr("fill", "#2a6200")
        .attr("stroke", "#fff")
        .attr("stroke-width", 1.5)
        .style("pointer-events", "none")
        .style("opacity", 0);

      const crosshairV = g.append("line")
        .attr("stroke", "#666")
        .attr("y1", 0)
        .attr("y2", innerHeight)
        .style("pointer-events", "none")
        .style("opacity", 0);

      const crosshairH = g.append("line")
        .attr("stroke", "#666")
        .attr("x1", 0)
        .attr("x2", innerWidth)
        .style("pointer-events", "none")
        .style("opacity", 0);

      /* =========================
         TITLES & LABELS
         ========================= */
      svg.append("text")
        .attr("x", (width / 2) + 30)
        .attr("y", 30)
        .attr("text-anchor", "middle")
        .style("font-size", "20px")
        .style("fill", "#555")
        .style("font-weight", "bold")
        .text("Inverter MPPT Profile");

      g.append("text")
        .attr("transform", "rotate(-90)")
        .attr("x", -innerHeight / 2)
        .attr("y", -55)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("font-weight", "bold")
        .text("Avg. Inverter V DC");

      g.append("text")
        .attr("x", innerWidth / 2)
        .attr("y", innerHeight + 60)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("font-weight", "bold")
        .text("Avg. POA Irradiance, W/m2");

      /* =========================
         INTERACTION OVERLAY
         (must be the topmost element so it captures pointer + wheel events)
         ========================= */
      const overlay = g
        .append("rect")
        .attr("class", "interaction-overlay")
        .attr("width", innerWidth)
        .attr("height", innerHeight)
        .attr("fill", "transparent")
        .style("cursor", "crosshair");

      const DOT_R = 4;
      const SNAP_RADIUS = 12;

      let xScale = xBase;
      let yScale = yBase;
      let quadtree = null;

      function applyTransform(transform) {
        transformRef.current = transform;
        xScale = transform.rescaleX(xBase);
        yScale = transform.rescaleY(yBase);

        const [xMin, xMax] = xScale.domain();
        const [yMin, yMax] = yScale.domain();

        // Prefer the "round" tick values from the un-zoomed view (multiples of
        // 200), but fall back to d3's auto-ticking when zoomed in far enough
        // that those round ticks become sparse.
        const baseXTicks = d3
          .range(0, 1201, 200)
          .filter((v) => v >= xMin && v <= xMax);
        const baseYTicks = d3
          .range(0, 1401, 200)
          .filter((v) => v >= yMin && v <= yMax);

        const xTickValues = baseXTicks.length >= 4 ? baseXTicks : xScale.ticks(7);
        const yTickValues = baseYTicks.length >= 4 ? baseYTicks : yScale.ticks(7);

        // Gridlines
        gridYG.call(
          d3.axisLeft(yScale)
            .tickValues(yTickValues)
            .tickSize(-innerWidth)
            .tickFormat("")
        );
        gridYG.selectAll("line").attr("stroke", "#d9d9d9");
        gridYG.select(".domain").remove();

        gridXG.call(
          d3.axisBottom(xScale)
            .tickValues(xTickValues)
            .tickSize(-innerHeight)
            .tickFormat("")
        );
        gridXG.selectAll("line").attr("stroke", "#d9d9d9");
        gridXG.select(".domain").remove();

        // Axes
        axisYG.call(d3.axisLeft(yScale).tickValues(yTickValues));
        axisXG.call(d3.axisBottom(xScale).tickValues(xTickValues));

        // Scatter dots
        dotsG
          .selectAll("circle.mppt-dot")
          .data(dataRef.current)
          .join(
            (enter) =>
              enter
                .append("circle")
                .attr("class", "mppt-dot")
                .attr("r", DOT_R)
                .attr("fill", "#2a6200")
                .attr("opacity", 0.25)
                .style("pointer-events", "none")
                .attr("cx", (d) => xScale(d.x))
                .attr("cy", (d) => yScale(d.y)),
            (update) =>
              update
                .attr("cx", (d) => xScale(d.x))
                .attr("cy", (d) => yScale(d.y))
          );

        // Rebuild quadtree in (zoomed) pixel space so hover snapping
        // continues to work correctly at every zoom level.
        quadtree = d3.quadtree()
          .x((d) => xScale(d.x))
          .y((d) => yScale(d.y))
          .addAll(dataRef.current);
      }

      // Tracks whether the user is actively click-and-drag panning the chart
      // (only possible when zoomed in past the default 1x view).
      let isDragging = false;
      // After a real drag-pan, the trailing `click` event (if the browser
      // fires one) must not be treated as a click-to-select.
      let suppressNextClick = false;
      // Mouse hover state for the current draw cycle. The persistent
      // keyboard/click selection lives on `selectedRef` (component scope).
      let hoveredDatum = null;
      let hoveredEvent = null;

      const cursorForZoom = (k) => (k > 1 ? "grab" : "crosshair");

      const hideTooltip = () => {
        if (onTooltipRef.current) {
          onTooltipRef.current((t) => ({ ...t, visible: false }));
        } else {
          tooltip?.style("opacity", 0);
        }
      };

      const hideIndicator = () => {
        crosshairV.style("opacity", 0);
        crosshairH.style("opacity", 0);
        activeDot.style("opacity", 0);
      };

      // Show indicator + mouse-following tooltip for a hovered point.
      function showHover(d, sourceEvent) {
        const cx = xScale(d.x);
        const cy = yScale(d.y);
        crosshairV.attr("x1", cx).attr("x2", cx).style("opacity", 1);
        crosshairH.attr("y1", cy).attr("y2", cy).style("opacity", 1);
        activeDot.attr("cx", cx).attr("cy", cy).style("opacity", 1);

        if (onTooltipRef.current) {
          onTooltipRef.current({
            visible: true,
            x: sourceEvent.clientX,
            y: sourceEvent.clientY - 10,
            content: (
              <>
                <strong>X:</strong> {d.x}
                <br />
                <strong>Y:</strong> {d.y}
              </>
            ),
          });
        } else {
          tooltip
            ?.style("opacity", 1)
            .html(`<strong>X:</strong> ${d.x}<br/><strong>Y:</strong> ${d.y}`)
            .style("left", `${sourceEvent.pageX + 10}px`)
            .style("top", `${sourceEvent.pageY - 30}px`);
        }
      }

      // Show indicator + anchored tooltip for the persistent selection.
      // The tooltip sits above the data point (no pointer to follow), and
      // we hide everything when the selected point is outside the visible
      // zoomed area.
      function showSelected(d) {
        const cx = xScale(d.x);
        const cy = yScale(d.y);
        const inView =
          cx >= 0 && cx <= innerWidth && cy >= 0 && cy <= innerHeight;
        if (!inView) {
          hideIndicator();
          hideTooltip();
          return;
        }

        crosshairV.attr("x1", cx).attr("x2", cx).style("opacity", 1);
        crosshairH.attr("y1", cy).attr("y2", cy).style("opacity", 1);
        activeDot.attr("cx", cx).attr("cy", cy).style("opacity", 1);

        const svgEl = svgRef.current;
        if (!svgEl) return;
        const rect = svgEl.getBoundingClientRect();

        if (onTooltipRef.current) {
          onTooltipRef.current({
            visible: true,
            x: rect.left + margin.left + cx,
            y: rect.top + margin.top + cy - 12,
            content: (
              <>
                <strong>X:</strong> {d.x}
                <br />
                <strong>Y:</strong> {d.y}
              </>
            ),
          });
        } else {
          tooltip
            ?.style("opacity", 1)
            .html(`<strong>X:</strong> ${d.x}<br/><strong>Y:</strong> ${d.y}`)
            .style(
              "left",
              `${rect.left + window.scrollX + margin.left + cx + 10}px`
            )
            .style(
              "top",
              `${rect.top + window.scrollY + margin.top + cy - 30}px`
            );
        }
      }

      // Decide what (if anything) to render right now. Mouse hover wins
      // when present; otherwise fall back to the persisted selection
      // when the SVG has keyboard focus.
      function refreshIndicator() {
        if (hoveredDatum && hoveredEvent) {
          showHover(hoveredDatum, hoveredEvent);
          return;
        }
        if (
          selectedRef.current &&
          svgRef.current &&
          document.activeElement === svgRef.current
        ) {
          showSelected(selectedRef.current);
          return;
        }
        hideIndicator();
        hideTooltip();
      }

      function handleHover(mx, my, sourceEvent) {
        if (!dataRef.current?.length || !quadtree) return;
        const found = quadtree.find(mx, my, SNAP_RADIUS);
        if (!found) {
          hoveredDatum = null;
          hoveredEvent = null;
          refreshIndicator();
          return;
        }
        hoveredDatum = found;
        hoveredEvent = sourceEvent;
        showHover(found, sourceEvent);
      }

      // Default selection when the chart first gains focus without one:
      // the data point nearest the visible viewport center.
      function defaultSelection() {
        if (!dataRef.current?.length) return null;
        const cx0 = innerWidth / 2;
        const cy0 = innerHeight / 2;
        let best = null;
        let bestDist = Infinity;
        for (const d of dataRef.current) {
          const dx = xScale(d.x) - cx0;
          const dy = yScale(d.y) - cy0;
          const dist = dx * dx + dy * dy;
          if (dist < bestDist) {
            bestDist = dist;
            best = d;
          }
        }
        return best;
      }

      // Spatial-nav: pick the nearest data point in the requested
      // direction relative to the current selection. The 45° cone
      // (`|dx| > |dy|` for left/right, vice-versa for up/down) keeps
      // ArrowLeft from jumping to a point that's mostly above.
      function navigate(direction) {
        if (!dataRef.current?.length) return;
        if (!selectedRef.current) {
          selectedRef.current = defaultSelection();
          refreshIndicator();
          return;
        }
        const sel = selectedRef.current;
        const fromCx = xScale(sel.x);
        const fromCy = yScale(sel.y);
        let best = null;
        let bestDist = Infinity;
        for (const d of dataRef.current) {
          if (d === sel) continue;
          const dx = xScale(d.x) - fromCx;
          const dy = yScale(d.y) - fromCy;
          let inCone = false;
          if (direction === "left") inCone = dx < 0 && Math.abs(dx) > Math.abs(dy);
          else if (direction === "right") inCone = dx > 0 && Math.abs(dx) > Math.abs(dy);
          else if (direction === "up") inCone = dy < 0 && Math.abs(dy) > Math.abs(dx);
          else if (direction === "down") inCone = dy > 0 && Math.abs(dy) > Math.abs(dx);
          if (!inCone) continue;
          const dist = dx * dx + dy * dy;
          if (dist < bestDist) {
            bestDist = dist;
            best = d;
          }
        }
        if (!best) return;
        selectedRef.current = best;

        // If the new selection would be off-screen at the current zoom,
        // pan to center it. `translateTo` takes coordinates in the
        // original (un-rescaled) viewport, so we feed it `xBase/yBase`.
        //
        // Important: this check must match the bounds `showSelected` uses
        // to decide whether to render the indicator (strict 0..innerW/H).
        // If we treated "almost in view" as in-view here, we'd skip the
        // pan and then `showSelected` would silently hide the indicator
        // for points sitting just past the edge — making it look like the
        // arrow key did nothing.
        const newCx = xScale(best.x);
        const newCy = yScale(best.y);
        const inView =
          newCx >= 0 && newCx <= innerWidth &&
          newCy >= 0 && newCy <= innerHeight;
        if (!inView) {
          overlay
            .transition()
            .duration(150)
            .call(zoom.translateTo, xBase(best.x), yBase(best.y));
          // The zoom 'zoom' handler will refresh the indicator each tick.
        } else {
          refreshIndicator();
        }
      }

      // +/- zoom. The focal point is the selected data point's current
      // pixel position, so the selection stays visually anchored under
      // the zoom (it remains under the same screen pixel before and
      // after, subject to the translateExtent clamp at MIN_ZOOM).
      function zoomBy(steps) {
        const t = transformRef.current;
        const factor = Math.pow(ZOOM_STEP, steps);
        const targetK = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, t.k * factor));
        if (targetK === t.k) return;
        if (!selectedRef.current) {
          selectedRef.current = defaultSelection();
          if (!selectedRef.current) return;
        }
        const sel = selectedRef.current;
        const px = xScale(sel.x);
        const py = yScale(sel.y);
        overlay
          .transition()
          .duration(150)
          .call(zoom.scaleBy, targetK / t.k, [px, py]);
      }

      overlay
        .on("mousemove", (event) => {
          if (isDragging) return;
          const [mx, my] = d3.pointer(event);
          handleHover(mx, my, event);
        })
        .on("mouseleave", () => {
          if (isDragging) return;
          hoveredDatum = null;
          hoveredEvent = null;
          // Falls back to the persistent selection if the SVG still has
          // keyboard focus, otherwise hides everything.
          refreshIndicator();
        })
        .on("click", (event) => {
          if (suppressNextClick) {
            suppressNextClick = false;
            return;
          }
          if (isDragging) return;
          const [mx, my] = d3.pointer(event);
          const found = quadtree?.find(mx, my, SNAP_RADIUS);
          if (found) {
            selectedRef.current = found;
          }
          // Focus the SVG so subsequent arrow / +/- keys are captured.
          svgRef.current?.focus({ preventScroll: true });
          refreshIndicator();
        });

      /* =========================
         ZOOM + PAN
         - wheel up   -> zoom in toward pointer
         - wheel down -> zoom out, clamped at the default 1x view
         - click+drag -> pan (only when zoomed in; constrained to the
                         original axis bounds via translateExtent)
         - keyboard   -> +/- via `zoomBy`, anchored on the selection
         ========================= */
      const zoom = d3.zoom()
        .scaleExtent([MIN_ZOOM, MAX_ZOOM])
        .extent([
          [0, 0],
          [innerWidth, innerHeight],
        ])
        .translateExtent([
          [0, 0],
          [innerWidth, innerHeight],
        ])
        // Allow wheel zoom always; allow drag-pan (mousedown) only when the
        // user is already zoomed in. Block double-click zoom and touch.
        .filter((event) => {
          if (event.type === "wheel") return true;
          if (event.type === "mousedown") {
            return transformRef.current.k > MIN_ZOOM;
          }
          return false;
        })
        .on("start", (event) => {
          // A drag-pan starts with a mousedown source event. Reset the
          // dragging flag — we'll latch it on the first drag-driven
          // "zoom" event so plain clicks (no movement) don't suppress
          // the hover tooltip.
          if (event.sourceEvent && event.sourceEvent.type === "mousedown") {
            isDragging = false;
          }
        })
        .on("zoom", (event) => {
          applyTransform(event.transform);

          const sourceEvent = event.sourceEvent;
          if (!sourceEvent) {
            // Programmatic zoom (keyboard +/- or navigate auto-pan).
            // Re-render the persistent indicator at the new pixel coords.
            refreshIndicator();
            return;
          }

          if (sourceEvent.type === "mousemove") {
            // Drag-pan: latch dragging state, swap to grabbing cursor,
            // and clear any lingering hover affordances.
            if (!isDragging) {
              isDragging = true;
              overlay.style("cursor", "grabbing");
              hoveredDatum = null;
              hoveredEvent = null;
              hideIndicator();
              hideTooltip();
            }
          } else if (sourceEvent.type === "wheel") {
            // Wheel zoom: keep cursor in sync with the new zoom level
            // and re-evaluate hover at the pointer's current location.
            overlay.style("cursor", cursorForZoom(event.transform.k));
            const [mx, my] = d3.pointer(sourceEvent, overlay.node());
            handleHover(mx, my, sourceEvent);
          }
        })
        .on("end", () => {
          if (isDragging) {
            isDragging = false;
            // A real drag happened — suppress the synthesized click that
            // browsers may fire on mouseup so click-to-select isn't
            // triggered at the end of a pan gesture.
            suppressNextClick = true;
          }
          overlay.style("cursor", cursorForZoom(transformRef.current.k));
        });

      overlay.style("cursor", cursorForZoom(transformRef.current.k));
      overlay.call(zoom);

      // Restore any prior zoom state (preserved across resizes / re-renders).
      // Setting the transform via the behavior fires the "zoom" handler,
      // which performs the initial render.
      overlay.call(zoom.transform, transformRef.current);

      // Publish this draw cycle's nav/zoom/refresh hooks so the effect-
      // scope keyboard listeners always drive the latest chart instance.
      handlersRef.current = {
        navigate,
        zoomBy,
        refresh: refreshIndicator,
        ensureDefaultSelection: () => {
          if (!selectedRef.current) {
            selectedRef.current = defaultSelection();
          }
        },
      };

      // If the chart was already focused when this draw ran (e.g. a
      // resize or data refresh while the user was navigating), re-render
      // the selection so the indicator persists across redraws.
      if (svgRef.current && document.activeElement === svgRef.current) {
        if (!selectedRef.current) {
          selectedRef.current = defaultSelection();
        }
        refreshIndicator();
      }
    }

    /* =========================
       LOAD DATA ONCE
       ========================= */
    let cancelled = false;

    const normalizeRows = (payload) => {
      const rows = Array.isArray(payload)
        ? payload
        : typeof payload === "string"
          ? JSON.parse(payload)
          : [];
      return rows
        .map((d) => ({
          x: Number(d.x),
          y: Number(d.y),
        }))
        .filter(
          (d) => Number.isFinite(d.x) && Number.isFinite(d.y)
        );
    };

    const hasProvidedData = Array.isArray(data) && data.length > 0;

    const load = async () => {
      if (hasProvidedData) {
        dataRef.current = normalizeRows(data);
        draw();
        return;
      }
      if (!portfolioName || !powerblockId) return;
      try {
        const query = new URLSearchParams({
          device: "powerblock",
          powerblockId: String(powerblockId),
        });
        if (startDate && endDate) {
          query.set("start", startDate);
          query.set("end", endDate);
        }
        const payload = await apiFetch(`/api/graph/portfolio/${portfolioName}/insights/${encodeURIComponent(duration)}/mppt?${query}`);
        if (cancelled) return;
        dataRef.current = normalizeRows(payload);
        draw();
      } catch (err) {
        console.warn("Inverter MPPT profile API fetch failed:", err);
      }
    };

    load();

    /* =========================
       RESIZE HANDLING
       ========================= */
    const observer = new ResizeObserver(draw);
    observer.observe(containerRef.current);

    /* =========================
       KEYBOARD NAVIGATION
       - Tab to focus, click on a data point to select.
       - Arrow keys move the selection in their direction (45° cones).
       - + / = zoom in toward the selected point.
       - - / _ zoom out, keeping the selected point under the same pixel.
       Listeners live at effect scope and read from `handlersRef` so they
       always drive whichever draw cycle is currently active.
       ========================= */
    const svgEl = svgRef.current;
    const dirMap = {
      ArrowLeft: "left",
      ArrowRight: "right",
      ArrowUp: "up",
      ArrowDown: "down",
    };

    const handleKeyDown = (e) => {
      const h = handlersRef.current;
      if (!h) return;
      if (dirMap[e.key]) {
        e.preventDefault();
        h.navigate(dirMap[e.key]);
        return;
      }
      if (e.key === "+" || e.key === "=" || e.code === "NumpadAdd") {
        e.preventDefault();
        h.zoomBy(1);
        return;
      }
      if (e.key === "-" || e.key === "_" || e.code === "NumpadSubtract") {
        e.preventDefault();
        h.zoomBy(-1);
      }
    };

    const handleFocus = () => {
      const h = handlersRef.current;
      if (!h) return;
      h.ensureDefaultSelection();
      h.refresh();
    };

    const handleBlur = () => {
      const h = handlersRef.current;
      if (!h) return;
      // refreshIndicator() now sees `document.activeElement !== svgEl`
      // and hides the selection indicator + tooltip.
      h.refresh();
    };

    svgEl?.addEventListener("keydown", handleKeyDown);
    svgEl?.addEventListener("focus", handleFocus);
    svgEl?.addEventListener("blur", handleBlur);

    return () => {
      cancelled = true;
      observer.disconnect();
      tooltip?.remove?.();
      svgEl?.removeEventListener("keydown", handleKeyDown);
      svgEl?.removeEventListener("focus", handleFocus);
      svgEl?.removeEventListener("blur", handleBlur);
    };
  }, [portfolioName, duration, startDate, endDate, powerblockId, data]);

  return (
    <div ref={containerRef} style={{ width: "100%", minWidth: "0" }}>
      <svg
        ref={svgRef}
        tabIndex={0}
        role="img"
        aria-label="Inverter MPPT scatter plot. Tab or click to select a data point; use arrow keys to move the selection; use + and - to zoom in and out around the selected point."
        style={{ display: "block" }}
      />
    </div>
  );
}
